import type { TeamChoice } from '@ghp/domain'
import { Button, Eyebrow, Field, InfoNote, Input, TextLink } from '@ghp/ui'
import { createRoute, redirect, useNavigate } from '@tanstack/react-router'
import { Lock, ShieldCheck, User } from 'lucide-react'
import { useId, useState } from 'react'

import { rootRoute } from '../../../app/routes/root'
import { failureFor, faultOf, incompleteEntry, teamLabel, type LoginFailure } from '../sign-in'
import s from './login.module.css'

/** Déjà entré — en ligne ou en dégradé — on ne repasse pas par la connexion. */
export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginScreen,
  beforeLoad: async ({ context }) => {
    await context.auth.ready
    if (context.auth.getSnapshot().state !== 'signed_out') {
      throw redirect({ to: '/' })
    }
  },
})

/**
 * Demande au navigateur de ne plus évincer le stockage de l'app, et dit s'il a refusé.
 *
 * Sans cette garantie, IndexedDB peut être vidée sous pression disque (D22) : la session,
 * la tournée et les pièces en attente d'envoi partiraient avec — et l'agent le découvrirait
 * au rechargement suivant, hors réseau, devant une maison.
 *
 * ⚠️ Un navigateur sans l'API ne refuse pas, il ne répond rien : rien à afficher, et rien
 * que l'agent puisse faire. Seul un refus formulé remonte.
 */
async function persistentStorageRefused(): Promise<boolean> {
  const storage: StorageManager | undefined = navigator.storage
  if (typeof storage?.persist !== 'function') return false

  try {
    return !(await storage.persist())
  } catch {
    return false
  }
}

/**
 * Connexion en deux temps (M6) : identifiants d'abord ; si l'agent appartient à plusieurs
 * équipes, le serveur n'émet aucun jeton et renvoie la liste — le second temps rappelle
 * `signIn` avec l'équipe choisie.
 */
function LoginScreen() {
  const { auth } = loginRoute.useRouteContext()
  const navigate = useNavigate()
  const failureId = useId()

  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')
  const [teams, setTeams] = useState<TeamChoice[] | null>(null)
  const [failure, setFailure] = useState<LoginFailure | null>(null)
  const [storageAtRisk, setStorageAtRisk] = useState(false)
  const [pending, setPending] = useState(false)
  const [forgotten, setForgotten] = useState(false)

  async function submit(teamId?: TeamChoice['id']): Promise<void> {
    const incomplete = incompleteEntry({ login, password })
    setFailure(incomplete)
    if (incomplete !== null) return

    setPending(true)
    try {
      const result = await auth.signIn({ login, password, teamId })
      if (result.outcome === 'team_selection_required') {
        setTeams(result.teams)
        return
      }

      // D22 demande la persistance **au moment de la connexion** : c'est le dernier instant
      // où l'agent est au dépôt, avec du réseau et quelqu'un à prévenir. Un refus ne défait
      // pas la connexion — elle a abouti — il retient l'agent le temps de le lire (M21).
      if (await persistentStorageRefused()) {
        setStorageAtRisk(true)
        return
      }

      await navigate({ to: '/' })
    } catch (thrown) {
      setFailure(failureFor(thrown))
    } finally {
      setPending(false)
    }
  }

  return (
    <main className={s.screen}>
      {/*
       * Le panneau de marque du prototype. `aria-hidden` : tout ce qu'il porte est de la
       * promotion — le nom de l'app, sa baseline — que le lecteur d'écran retrouve dans le
       * titre du document. L'annoncer avant le formulaire ferait traverser un paragraphe de
       * marketing à chaque connexion.
       *
       * Deux éléments du prototype manquent ici, et c'est délibéré : « Rester connecté »
       * (la session terrain est toujours persistée — D22, la case n'aurait rien à décider) et, au
       * pied, « dernière synchro » (rien n'enregistre cette date aujourd'hui) — seule la version y
       * reste. « Mot de passe oublié ? » est là, mais n'envoie rien : un compte d'équipe est
       * partagé, il n'a pas de boîte où recevoir un lien (M6, D3) — c'est le bureau qui le
       * réinitialise, et le lien le dit.
       */}
      <aside className={s.brandPanel} aria-hidden>
        <div className={s.logoRow}>
          <span className={s.mark}>GHP</span>
          <span>
            <span className={s.wordmark}>GHP Pilot</span>
            <span className={s.since}>Terrain · Depuis 1996</span>
          </span>
        </div>
        <div className={s.pitchBlock}>
          <p className={s.pitch}>Contrôle d’assainissement sur le terrain.</p>
          <p className={s.lede}>
            Vos contrôles planifiés, fiches terrain et schémas — synchronisés et disponibles hors
            ligne.
          </p>
        </div>
        <p className={s.version}>{import.meta.env.VITE_APP_VERSION || 'dev'}</p>
      </aside>

      <div className={s.formPanel}>
        <div className={s.panel}>
          <Eyebrow>Espace contrôleur</Eyebrow>
          <h1 className={s.title}>Connexion</h1>

          {storageAtRisk ? (
            <div className={s.notice}>
              <p role="alert">
                Connexion réussie, mais la tablette n’a pas garanti son stockage : si l’espace vient
                à manquer, elle peut effacer les contrôles et les pièces en attente d’envoi. Faire
                de la place, et prévenir le bureau avant de partir.
              </p>
              <Button tone="brand" fullWidth onClick={() => void navigate({ to: '/' })}>
                Continuer
              </Button>
            </div>
          ) : teams === null ? (
            <form
              className={s.form}
              onSubmit={(event) => {
                event.preventDefault()
                void submit()
              }}
            >
              <Field label="Identifiant">
                {({ id }) => (
                  <Input
                    id={id}
                    name="login"
                    autoComplete="username"
                    autoCapitalize="none"
                    icon={<User size={17} aria-hidden />}
                    value={login}
                    onValueChange={setLogin}
                    {...faultOf(failure, 'login', failureId)}
                  />
                )}
              </Field>
              <Field label="Mot de passe">
                {({ id }) => (
                  // La bascule de révélation vient de `type="password"` : au doigt ou au stylet,
                  // c'est le seul moyen de relire une saisie masquée avant de l'envoyer.
                  <Input
                    id={id}
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    icon={<Lock size={17} aria-hidden />}
                    value={password}
                    onValueChange={setPassword}
                    {...faultOf(failure, 'password', failureId)}
                  />
                )}
              </Field>
              <div className={s.formRow}>
                <TextLink aria-expanded={forgotten} onClick={() => setForgotten(true)}>
                  Mot de passe oublié ?
                </TextLink>
              </div>
              {forgotten && (
                <InfoNote>
                  Le mot de passe de la tablette se réinitialise au bureau : un gestionnaire en tire
                  un nouveau depuis la fiche de l’équipe, et vous le transmet.
                </InfoNote>
              )}
              <Button tone="brand" type="submit" fullWidth disabled={pending}>
                {pending ? 'Connexion…' : 'Se connecter'}
              </Button>
            </form>
          ) : (
            <div className={s.teams}>
              <p>{pending ? "Connexion à l'équipe…" : "Choisir l'équipe de la tournée :"}</p>
              {teams.map((team) => (
                <Button
                  key={team.id}
                  tone="neutral"
                  fullWidth
                  disabled={pending}
                  onClick={() => void submit(team.id)}
                >
                  {teamLabel(team, teams)}
                </Button>
              ))}
            </div>
          )}

          {failure !== null && (
            <p id={failureId} className={s.error} role="alert">
              {failure.message}
            </p>
          )}

          <p className={s.footnote}>
            <ShieldCheck size={16} aria-hidden />
            Connexion sécurisée · accès réservé aux agents GHP
          </p>
        </div>
      </div>
    </main>
  )
}
